import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { renderToString } from 'react-dom/server';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import * as authActions from '../actions/authActions';
import * as sessionActions from '../actions/sessionActions';
import * as profileActions from '../actions/profileActions';
import * as policyActions from '../actions/policyActions';
import * as vehicleActions from '../actions/vehicleActions';
import * as addressActions from '../actions/addressActions';
import { profileSelector, policySelector } from '../selectors';
import { push } from 'connected-react-router';
import { useParams, Redirect } from 'react-router-dom';
import ActionSelection from '../components/sections/policy/change/ActionSelection';
import SelectEffectiveDate from '../components/sections/policy/change/SelectEffectiveDate';
import styles from './PolicyUpdatePage.module.scss';
import { Button, Dimmer, Loader, Message, Header, Modal } from 'semantic-ui-react';
import CancelModal from '../components/sections/policy/change/CancelModal';
import updateActions from '../components/sections/policy/change/Actions';
import SavedDataModal from '../components/sections/policy/change/SavedDataModal';
import ErrorModal from '../components/ErrorModal';
import HelpModal from '../components/sections/policy/change/HelpModal';
import NavigationPrompt from 'react-router-navigation-prompt';
import moment from 'moment';

const PolicyUpdatePage = ({ actions, profile, policies }) => {
	let { policyNumber } = useParams();

	const [action, setAction] = useState(null);
	const [effectiveDate, setEffectiveDate] = useState(null);
	const [showActionCancel, setShowActionCancel] = useState(false);
	const [error, setError] = useState(null);
	const [hasSave, setHasSave] = useState(false);
	const [useSave, setUseSave] = useState(false);

	const [save, setSave] = useState(null);
	const [rate, setRate] = useState(null);

	const [loading, setLoading] = useState(null);
	const [exit, setExit] = useState(false);

	const onActionCancel = useCallback(() => {
		setShowActionCancel(true);
	}, []);

	useEffect(() => {
		const load = async () => {
			try {
				const profileResult = await actions.getProfile();

				if (profileResult.response.policies.length > 0) {
					await actions.getPolicies(profileResult.response.policies);
				}
			} catch (error) {
				setError('Failed to load policy');
			}
		};

		if (!profile.loaded) {
			load();
		}
	}, [actions, profile.loaded]);

	const policy = useMemo(() => policies.items.find((p) => p.policyNumber === policyNumber), [policies, policyNumber]);

	const onComplete = useCallback(
		async (rate) => {
			let _emails = policy.agency.emails || [];

			try {
				setLoading('Finalizing changes');

				var { emailDetailsComponent: EmailDetailsComponent } = action;
				const emailDetails = EmailDetailsComponent ? renderToString(<EmailDetailsComponent policy={policy} rate={rate} />) : null;

				await actions.rateConfirm({ policyId: policy.policyId, policyNumber: policy.policyNumber, policyImageNum: rate.policyImageNum, effectiveDate: effectiveDate, agencyEmailAddress: _emails[0], emailDetails, policyHolderFirstName: policy.policyHolder.firstName, policyHolderLastName: policy.policyHolder.lastName });
				await actions.deleteSave(policy.policyNumber);
				setLoading(null);
			} catch (error) {
				setError(error.error || error.message);
				//Delete the save even in the case of a rate confirm failure
				await actions.deleteSave(policy.policyNumber);
				setLoading(null);
				throw error;
			}
		},
		[action, actions, effectiveDate, policy]
	);

	useEffect(() => {
		const load = async () => {
			try {
				setLoading('Loading Save');
				const saveResult = await actions.getSave(policy.policyNumber);
				let _save = saveResult.response;
				const _hasSave = !!_save;

				if (_hasSave) {
					const data = JSON.parse(_save.data);
					if (data.rate && !policy.hasPendingEndorsement) {
						await actions.deleteSave(policy.policyNumber);
					} else {
						setSave(_save);
						setRate(data.rate);
						setHasSave(_hasSave);
					}
				}

				setUseSave(false);
				setLoading(null);
			} catch (error) {
				setLoading(null);
				setError(error.error);
			}
		};

		if (policy) {
			load();
		}
	}, [actions, policy]);

	const onUseSave = useCallback(() => {
		const _updateActions = updateActions(policy);
		const allUpdateActions = _updateActions.flatMap((s) => s.actions);
		const currentAction = allUpdateActions.find((a) => a.data.type === save.type && a.data.action === save.action);
		setAction(currentAction);
		setUseSave(true);
		setEffectiveDate(save.effectiveDate);
		setHasSave(false);
	}, [policy, save]);

	const onNoSave = useCallback(async () => {
		try {
			setHasSave(false);
			setLoading('Deleting Save');
			await actions.deleteSave(policy.policyNumber);

			if (policy.hasPendingEndorsement) {
				await actions.deleteRate({ policyId: policy.policyId, policyNumber: policy.policyNumber, endorsementPolicyImageNumber: policy.pendingEndorsementPolicyImageNum, transactionDate: policy.pendingEndorsementTransactionDate });
			}

			setSave(null);
			setRate(null);
			setUseSave(false);
			setLoading(null);
		} catch (error) {
			setLoading(null);
			setHasSave(false);
			setError('Failed to clear save: ' + error);
		}
	}, [actions, policy]);

	const Component = useMemo(() => (action ? action.component : null), [action]);

	const onSave = useCallback(
		async (actionData, rate) => {
			try {
				let _emails = policy.agency.emails || [];

				setLoading('Saving');
				await actions.save({ ...action.data, data: JSON.stringify({ ...actionData, rate }), id: save?.id, effectiveDate, policyId: policy.policyId, policyNumber: policy.policyNumber, policyImageNum: policy.policyImageNum, agencyEmailAddress: _emails[0], agencyName: policy.agency.displayName });
				setLoading(null);
			} catch (error) {
				setLoading(null);
				setError(error);
			}
		},
		[action, actions, effectiveDate, policy, save]
	);

	const onExit = useCallback(() => {
		setExit(true);
	}, []);

	const onCancel = useCallback(async () => {
		setExit(true);
	}, []);

	const onActionCancelConfirm = useCallback(async () => {
		setShowActionCancel(false);
		setLoading('Canceling');

		if (rate) {
			await actions.deleteRate({ policyId: policy.policyId, policyNumber: policy.policyNumber, endorsementPolicyImageNumber: rate.policyImageNum, transactionDate: rate.transactionDate });
		}

		await actions.deleteSave(policy.policyNumber);

		setAction(null);
		setUseSave(false);
		setSave(null);
		setRate(null);
		setEffectiveDate(null);
		setLoading(null);
	}, [actions, policy, rate]);

	const onErrorClear = useCallback(() => {
		setError(null);
		onCancel();
	}, [onCancel]);

	const onClearRate = useCallback(async () => {
		if (rate) {
			await actions.deleteRate({ policyId: policy.policyId, policyNumber: policy.policyNumber, endorsementPolicyImageNumber: rate.policyImageNum, transactionDate: rate.transactionDate });
			setRate(null);
		}
	}, [actions, policy, rate]);

	useEffect(() => {
		if (error && policy && policy.agency.emails && policy.agency.emails.length > 0) {
			let _emails = policy.agency.emails || [];

			var _error = error;
			if (Array.isArray(_error)) {
				_error = _error.join(', ');
			}

			try {
				actions.sendErrorEmail({
					policyNumber: policy.policyNumber,
					agencyEmailAddress: _emails[0],
					policyHolderName: policy.policyHolder.displayName,
					policyHolderAddress1: policy.addresses[0].line1,
					policyHolderAddress2: policy.addresses[0].line2,
					policyHolderPhone: profile.phoneNumber,
					policyHolderEmail: profile.email,
					errorMessage: _error,
				});

				console.log('Sent error email');
			} catch {
				console.log('Failed to send error email');
			}
		}
	}, [actions, error, policy, profile]);

	const isEffectiveDateValid = useMemo(() => {
		if (!policy) {
			return false;
		}

		const min = moment(policy.minimumEffectiveDate);
		const max = moment(policy.maximumEffectiveDate);

		return effectiveDate && moment(effectiveDate).isBetween(min, max, 'day', '[]');
	}, [effectiveDate, policy]);

	return (
		<div className={styles.container}>
			{exit && <Redirect to='/' />}
			<div className={styles.header}>
				<Header>
					{policyNumber} - {action ? action.header : 'Make a Change'}
				</Header>

				{policy && (
					<HelpModal
						title='Agency Information'
						agency={policy.agency}
						policyNumber={policy.policyNumber}
						trigger={
							<Button id='agencyBtn' primary className={styles.help}>
								Agency Information
							</Button>
						}
					/>
				)}
			</div>

			<CancelModal open={showActionCancel} onConfirm={onActionCancelConfirm} onClose={() => setShowActionCancel(false)} />
			<SavedDataModal open={hasSave} onClose={() => setSave(null)} onYes={onUseSave} onNo={onNoSave} />

			{loading && (
				<Dimmer active>
					<Loader active size='massive'>
						{loading}
					</Loader>
				</Dimmer>
			)}

			{!action && (
				<div>
					{error && (
						<div>
							<Message error icon='warning'>
								Error: {error}
							</Message>
							<div className={styles.buttons}>
								<Button primary onClick={onCancel}>
									Cancel
								</Button>
								<Button primary onClick={onErrorClear}>
									Back
								</Button>
							</div>
						</div>
					)}
					{!error && (
						<div>
							<ActionSelection onSelect={setAction} onCancel={onCancel} policy={policy} />
						</div>
					)}
				</div>
			)}
			{action && (
				<div>
					<NavigationPrompt
						when={(_, nextLocation) => {
							return !exit && (!nextLocation.pathname || !nextLocation.pathname.includes('mailto')) && !loading;
						}}>
						{({ onConfirm, onCancel }) => {
							return (
								<Modal open={true} size='tiny'>
									<Modal.Header>Leaving?</Modal.Header>
									<Modal.Content>
										If you leave now your changes <b>will not</b> be saved. Are you sure?
									</Modal.Content>
									<Modal.Actions>
										<Button primary onClick={onConfirm}>
											Yes
										</Button>
										<Button primary onClick={onCancel}>
											No
										</Button>
									</Modal.Actions>
								</Modal>
							);
						}}
					</NavigationPrompt>
					<ErrorModal open={!!error} agency={policy.agency} onClear={onErrorClear} error={error} policyNumber={policy.policyNumber}>
						<div>Unfortunately we were not able to process the change you submitted to your policy ({policyNumber}) at this time. Please contact your agent at the following to discuss. Your agent will be able to move forward submitting the change.</div>
					</ErrorModal>

					{!isEffectiveDateValid && !hasSave && <SelectEffectiveDate onSelect={setEffectiveDate} onCancel={onActionCancel} min={policy.minimumEffectiveDate} max={policy.maximumEffectiveDate} message={effectiveDate ? 'The saved effective date is now invalid, please update when you want your policy change to be effective.' : 'When do you want your policy change to be effective?'} showFraudWarning={action.showFraudWarning} />}
					{isEffectiveDateValid && effectiveDate && Component && (
						<div>
							<Component actions={actions} policy={policy} profile={profile} effectiveDate={effectiveDate} onCancel={onActionCancel} onComplete={onComplete} onError={setError} save={useSave ? JSON.parse(save.data) : null} rate={rate} setRate={setRate} setLoading={setLoading} onSave={onSave} onExit={onExit} onClearRate={onClearRate} error={error} />
						</div>
					)}
				</div>
			)}
		</div>
	);
};

PolicyUpdatePage.propTypes = {
	actions: PropTypes.object.isRequired,
	policies: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => {
	return {
		profile: profileSelector(state),
		policies: policySelector(state),
	};
};

const mapDispatchToProps = (dispatch) => ({
	actions: {
		...bindActionCreators({ push }, dispatch),
		...bindActionCreators(authActions, dispatch),
		...bindActionCreators(profileActions, dispatch),
		...bindActionCreators(policyActions, dispatch),
		...bindActionCreators(sessionActions, dispatch),
		...bindActionCreators(vehicleActions, dispatch),
		...bindActionCreators(addressActions, dispatch),
	},
});

export default connect(mapStateToProps, mapDispatchToProps)(PolicyUpdatePage);
